// License-related filters

import { CANONICAL_LICENSE_STATUSES } from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const licenseFilters: FilterRegistry = {
	licenseKeywords: {
		// No fulltextFields here on purpose — the shared filter builder would
		// only match against `licenseNumber` and `type`, but licenses also
		// need to match by client name (which lives across the address join).
		// licenses.service.ts handles `licenseKeywords` manually with the
		// proper join. Adding fulltextFields here would cause the shared
		// builder to apply a *narrower* clause that gets ANDed with the
		// service's clause, hiding licenses that only match by client name.
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search licenses...',
			objectTable: 'client_license',
			listKeys: ['license', 'client_licenses'],
		}),
		queryParamName: 'licenseKeywords',
	},

	licenseStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseStatus',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: [
			// Derived from the canonical vocabulary (PD-14745) so the dropdown
			// never drifts from lm_status_canonical / the dashboard chart buckets.
			...CANONICAL_LICENSE_STATUSES.map((s) => ({ id: s, label: s })),
			// 'Other' = the catch-all bucket on the dashboard chart: any effective
			// status that isn't one of the canonical values above (legacy
			// free-text on the local column). Surfaced so the chart segment is
			// drillable. The custom handler in typeormFilterBuilder.ts translates
			// this to a NOT IN list.
			{ id: 'Other', label: 'Other' },
		],
		// 'Expired' is not reliably stored on the row — the custom handler in
		// typeormFilterBuilder.ts derives it (stored-'Expired' OR Active with
		// a past expirationDate) over the effective status
		// (COALESCE(registry_status, status)), matching the dashboard KPI and
		// the row-level red pill. 'Active' symmetrically excludes date-expired
		// rows.
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses'],
	},

	// Restricts licenses by their owning client's lead/customer state.
	// The licenses list joins to client_address → client by default, so
	// the custom handler in typeormFilterBuilder.ts can filter on
	// `client.leadOrCustomer` directly. Used by the compliance dashboard
	// click-throughs to keep the count visible after drilling in
	// matching what the dashboard counted (customers only) — without it,
	// the list shows lead licenses too and the row count diverges from
	// the chart segment.
	licenseClientLeadOrCustomer: {
		type: 'radio',
		label: 'Client Type',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientLeadOrCustomer',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'Customer', label: 'Customers' },
			{ id: 'Lead', label: 'Leads' },
		],
		defaultValue: { id: 'All', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		listKeys: ['license', 'client_licenses'],
	},

	licenseType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/type/options',
		queryParamName: 'licenseType',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'type',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses'],
	},

	// PD-15539 — the single "State" filter (`licenseState`) is RETIRED. It
	// evaluated a COALESCE chain that answered with the issuing board on matched
	// rows and the client's location on everything else, so one label selected
	// two different populations. Product decision 2026-08-24: issuing state and
	// client location are two different facts and must never be conflated in a
	// label, filter or coverage metric. They are now the two filters below.
	//
	// `licenses.service.ts` still ACCEPTS the old `licenseState` key so a saved
	// filter set or bookmarked URL keeps selecting what it always did; it is
	// simply not offered in the tray any more. Do not re-add it here.

	licenseIssuingState: {
		type: 'multi-select',
		label: 'Issuing State',
		placeholder: 'Select issuing state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		// The registry's own vocabulary, whitelisted to US jurisdictions
		// (PD-14528). This is the axis being filtered — which board granted the
		// licence — so the options come from the registry, not from the tenant's
		// address data.
		apiEndpoint: '/licenses/registry/state/options',
		queryParamName: 'licenseIssuingState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		// No queryMapping on purpose — the issuing state lives on the matched
		// `lm_registry` row, which the shared builder cannot reach from this
		// tenant-local surface. `licenses.service.ts` pre-resolves the matching
		// registry ids in a separate query and then filters locally. Mirrors the
		// `licenseKeywords` pattern above.
		format: 'array',
		// `license` only: the per-client licences list is served by a different
		// endpoint (ClientsService.getClientLicenses, PD-13787) which does not
		// implement these two handlers, so offering them there would no-op.
		listKeys: ['license'],
	},

	licenseClientLocationState: {
		type: 'multi-select',
		label: 'Client Location',
		placeholder: 'Select client location...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/state/options',
		queryParamName: 'licenseClientLocationState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		// No queryMapping on purpose — the client's location is on the joined
		// `client_address` row, not on `client_license`, so the shared builder
		// would emit a clause against the wrong table. `licenses.service.ts`
		// handles it manually against the `address` alias.
		format: 'array',
		// `license` only — same reason as the issuing-state filter above.
		listKeys: ['license'],
	},

	licenseClient: {
		...createEntityLookupFilter({
			label: 'Client',
			placeholder: 'Search clients...',
			apiEndpoint: '/clients',
			objectTable: 'client_license',
			fieldName: 'clientId',
			listKeys: ['license'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
			// `custom` (not `exists`): the actual SQL lives in the manual
			// `licenseClient` branch of LicensesService.findAll. The shared
			// `exists` branch in typeormFilterBuilder requires joinTable +
			// joinConditions metadata that we don't provide, so it no-ops
			// silently. Declaring `custom` makes the intent match reality and
			// prevents a future cleanup from removing the manual handler.
			joinType: 'custom',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'licenseClient',
		valueExtractor: {
			property: 'id',
		},
	},

	licenseExpirationDateRange: {
		...createDateRangeFilter({
			label: 'Expiration Date',
			objectTable: 'client_license',
			dateField: 'expirationDate',
			listKeys: ['license', 'client_licenses'],
		}),
		queryParamName: 'licenseExpirationDateRange',
		allowClear: true,
	},

	licenseHasDisciplinary: {
		type: 'radio',
		label: 'Has Disciplinary Action',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseHasDisciplinary',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'hasDisciplinaryAction',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'client_licenses'],
	},

	licenseSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/licenses/salesrep/options',
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'licenseSalesRep',
		multiple: true,
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// PD-13955 Phase A: filters by the state board's disciplinary FLAG on the
	// matched registry record (client_license.has_disciplinary_action). This
	// is the flag-only signal — licenses the board marks as having
	// disciplinary history but with no recorded detail rows yet. Distinct
	// from licenseHasDisciplinary above, which matches actual recorded
	// license_disciplinary_action rows (the At Risk tab / dashboard KPI).
	licenseBoardFlagged: {
		type: 'radio',
		label: 'Board Flagged',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseBoardFlagged',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// PD-13949 / PD-14745: filters matched licenses by whether their linked
	// registry record is under a board action, via the locally-mirrored
	// client_license.registry_status column (populated on confirm, kept fresh
	// by the nightly registry-value re-apply) — tenant-local per PD-13891.
	// `adverse` is the aggregate the Adverse Action tab and the dashboard KPI
	// pin: a confirmed match to a Suspended/Revoked/Surrendered/Denied/Probation
	// state license. (Cancelled/Expired are administrative lapses — "not
	// covered", surfaced via the status/Expired filters, not here.)
	// Link drift (2026-09-05): confirmed licences whose regulator link to
	// another licence the client holds has ENDED (the vet left the premise, the
	// PIC changed). Flag lives on the confirmed match row's match_signals,
	// written by the nightly link-drift stage — tenant-local, no cross-DB join.
	licenseLinkEnded: {
		type: 'radio',
		label: 'Link Ended',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseLinkEnded',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// PD-16226 — the Compliance dashboard's population and buckets, as list
	// filters, so a KPI card can deep-link to exactly the clients it counted.
	// Before this the "No Active License" card linked to the whole Pending
	// Manual Match tab (4,730 rows for republic against a card reading 82) and
	// no combination of the existing filters could reproduce it.
	licenseActiveBuyer: {
		type: 'radio',
		label: 'Active Buyer',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseActiveBuyer',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Buying (sale in 90 days)' },
			{ id: 'no', label: 'Not buying' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// The client's coverage bucket — the same one the dashboard's Coverage bar
	// and Needs Attention cards put each active buyer in. Client-level on
	// purpose: a client is covered once ANY of their licences is verified, so
	// "no active licence" cannot be expressed per licence row.
	licenseClientCoverage: {
		type: 'radio',
		label: 'Client Coverage',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientCoverage',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'no_active_license', label: 'No Active License' },
			{ id: 'unconfirmed', label: 'Unconfirmed Match Only' },
			{ id: 'verified', label: 'Verified' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// PD-16228 — the three remaining quality classes from the tenant value
	// ledger (maintenance/active/tenant-value-ledger.mjs), as list filters so a
	// reviewer can act on them instead of reading them in a CLI report. The
	// fourth class, a dead licence shown as good, is the "Not Usable at State"
	// option on licenseRegistryStanding.
	// PD-16229 (Chris, 2026-09-17): "we can definitely remove the clients in
	// closed/dead etc type lifecycle stages from the matching recommendations …
	// if they are set, it's super weird to see those in the list". The gate is
	// the lifecycle stage's own `is_closed_type` boolean — never a name
	// heuristic, and never the customer NAME, where republic's staff type
	// "*** CLOSED ***" (411 queue rows on 2026-09-17, none of them an active
	// buyer). A client with NO stage set is not excluded: absence of a stage is
	// not evidence the account is dead, and the feature is new enough that most
	// tenants have not filled it in (ndcd 145 clients today, republic none).
	licenseClientLifecycle: {
		type: 'radio',
		label: 'Client Lifecycle',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientLifecycle',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'exclude_closed', label: 'Hide Closed Accounts' },
			{ id: 'closed_only', label: 'Closed Accounts Only' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	licenseRegistryGap: {
		type: 'radio',
		label: 'Registry Disagreement',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRegistryGap',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// We are refusing or chasing renewals on an account the board says
			// is current — lost orders and wasted rep time.
			{
				id: 'false_block',
				label: 'False Block (we say expired, board says current)',
			},
			// The renewal is already on file at the board; our term is the old
			// one, so this becomes a false block the day it "expires".
			{
				id: 'stale_renewal',
				label: 'Stale Renewal (board holds a later term)',
			},
			// The account holds a lapsed licence while the board shows a
			// different active permit for the same location.
			{ id: 'superseded', label: 'Superseded Permit' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	licenseRegistryStanding: {
		type: 'radio',
		label: 'Adverse Action',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRegistryStanding',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// PD-16225: everything the state will not let the customer use —
			// board actions AND administratively dead licences (Cancelled,
			// Expired, Inactive, Retired, Deceased) — and it counts an
			// unconfirmed 90%+ suggestion as well as a confirmed link.
			{ id: 'unusable', label: 'Not Usable at State' },
			{ id: 'adverse', label: 'Any Adverse Action' },
			{ id: 'Suspended', label: 'Suspended' },
			{ id: 'Revoked', label: 'Revoked' },
			{ id: 'Surrendered', label: 'Surrendered' },
			{ id: 'Denied', label: 'Denied' },
			{ id: 'Probation', label: 'Probation' },
			{ id: 'Limited', label: 'Limited' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// PD-14745: filter matched licenses by order eligibility (can_order) of the
	// linked registry record — tenant-local via the mirrored registry_status
	// column. `can_order` = valid/in-force (Active/Probation/Limited); `cannot_order` =
	// everything else. Complements the Adverse Action filter above.
	licenseCanOrder: {
		type: 'radio',
		label: 'Order Eligibility',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseCanOrder',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'can_order', label: 'Can Order (valid / in-force)' },
			{ id: 'cannot_order', label: 'Cannot Order' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Filters licenses by match status: unmatched (no registry link) or pending review
	licenseMatchStatus: {
		type: 'radio',
		label: 'Match Status',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchStatus',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// 'matched' is the inverse of 'unmatched' — it selects every
			// license linked to a registry record (fk_registry_license__id
			// IS NOT NULL). Distinct from 'confirmed', which requires a
			// client_license_match row of status 'confirmed'; most matched
			// licenses came in via initial harvest backfill and never had
			// a review record. The compliance-dashboard "Matched" KPI and
			// the "Matched" segment of the By-Match chart drill into this.
			{ id: 'matched', label: 'Matched' },
			{ id: 'unmatched', label: 'Unmatched' },
			{ id: 'under_review', label: 'Under Review' },
			{ id: 'pending_review', label: 'Pending Review' },
			{ id: 'has_pending_match', label: 'Pending Match' },
			{ id: 'confirmed', label: 'Confirmed' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Coarse confidence-bucket filter for the License Matchmaker page.
	// Maps to the same client_license_match.confidence_score column as
	// licenseMatchConfidence, but encapsulates the thresholds so the UI
	// and the SQL agree. Boundaries are intentionally open-on-the-upper
	// side for medium/low (50<=x<85, x<50) so decimal scores (the column
	// is decimal(5,2)) don't fall through gaps between buckets.
	licenseMatchConfidenceLevel: {
		type: 'radio',
		label: 'Confidence',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchConfidenceLevel',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'high', label: 'High (≥85%)' },
			{ id: 'medium', label: 'Medium (50–84%)' },
			{ id: 'low', label: 'Low (<50%)' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license'],
	},

	// Filters licenses by the confidence score of their pending registry
	// match. Either bound may be omitted. The filter serializes to
	// `{ min, max }` on the wire (either can be null) via the same
	// property-extractor pattern used by date-range filters.
	licenseMatchConfidence: {
		type: 'number-range',
		label: 'Match Confidence (%)',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchConfidence',
		multiple: false,
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		min: 0,
		max: 100,
		step: 1,
		suffix: '%',
		listKeys: ['license'],
	},
};
